import { OrderTimelineKeyEnum } from "Infrastructure/Api/Api";
import type {
	OrderAllocationFlow,
	OrderArchive,
	OrderCallOutcome,
	OrderClaim,
	OrderCommentsTable,
	OrderCreateForm,
	OrderDetail,
	OrderEditForm,
	OrderHeaderButtons,
	OrderMutations,
	OrderPhotos,
	OrderProtocol,
	OrderShared,
	OrderTabs,
	OrderTranslations,
	PartnerReviewForm,
	PartnerReviewShared,
	SelectOptions,
	ValidationMessages,
} from "Infrastructure/Translation/Types/order";
import { TimelineActionEnum } from "Order/Enum/timelineActionEnum";
import type { PartnerReviewMutations } from "../Types/order";

const editFormTranslations: OrderEditForm = {
	generalData: "General data",
	address: "Address",
	cleaning: "Cleaning",
	currentColor: "Current color",
	floorArea: "Floor area",
	formSubmitDate: "Form submission date",
	movingFurniture: "Moving furniture",
	newColor: "New color",
	nextActionDate: "Next action date",
	note: "Information for customers and partners",
	numberOfShades: "Number of shades",
	paintProvidedByPainter: "Paint provided by painter",
	partnerId: "Partner ID",
	price: "Price",
	packageType: "Order package",
	packageTypeNone: "None",
	marketRegion: "Region",
	repair: "Repair",
	save: "Save",
	status: "Status",
	title: "Edit order",
	wallArea: "Wall area",
	partner: "Partner",
	partnerSaveButton: "Save",
	partnerWarning:
		"The assigned partner is not in the same region as the order.",
	partnerTitle: "Assigned partner",
	jobDurationDays: "Job duration override",
	jobDurationDaysHelper: "Leave empty to use automatic duration.",
	effectiveJobDurationDays: "Effective duration",
	jobDurationAuto: "Auto, currently {{days}} day(s)",
	jobDurationManual: "Manual override: {{days}} day(s)",
	reset: "Reset",
	dateDone: "Date done",
	dateStarting: "Start date",
	cancellationReason: "Cancellation reason",
	dateNextActionFrom: "Next action date from",
	dateNextActionTo: "Next action date to",
	user: "Manager",
	priceChangeReason: "Price change reason",
	cancel: "Cancel",
	change: "Change",
	changePrice: "Change price",
	hide: "Hide invoice information",
	show: "Show invoice information",
	comments: "Comments",
	dateStartingFrom: "Start date from",
	dateStartingTo: "Start date to",
	unassignedPartner: "Search for orders without assigned partner",
	unassignedUser: "Search for orders without assigned manager",
	review: "Review",
	notReviewed: "Search for orders without reviews",
	hasExamination: "Search for orders with examination",
	materialPrice: "Material price",
	b2b: "B2B",
	promoCode: "Promo code",
	zoneUpdateTitle: "Update transport zone?",
	zoneUpdateMessage:
		"The address has changed. Do you want to also update the transport zone?",
	zoneUpdateConfirm: "Yes, update zone",
	zoneUpdateCancel: "No",
};

const createFormTranslations: OrderCreateForm = {
	address: "Address",
	market: "Market",
	marketRegion: "Region",
	customerName: "Customer name",
	customerEmail: "Customer email",
	customerPhone: "Customer phone",
	vertical: "Vertical",
};

export const orderDetailTranslations: OrderDetail = {
	pageTitle: "Order Details",
	pageDescription: "Detailed information about the order",
	info: {
		titleGeneralInfo: "General info",
		titleAddress: "Address",
		titleCustomer: "Customer",
		status: "Status",
		dateNextAction: "Next action date",
		dateUpdated: "Date updated",
		customerName: "Customer",
		partnerName: "Partner",
		id: "ID",
		orderType: "Order type",
		price: "Price",
		street: "Street",
		city: "City",
		country: "Country",
		postCode: "Post code",
		name: "Name",
		email: "Email",
		phone: "Phone",
		dateCreated: "Date created",
		manager: "Manager",
		contact: "Contact",
		dateStarting: "Start date",
		invoicingName: "Invoicing name",
		invoicingAddress: "Invoicing address",
		businessId: "Business ID",
		claim: "Claim",
		examination: "Examination",
	},
	timeline: {
		title: "Timeline",
		statusChanged: "Status changed",
		orderStatusChange: "Order status changed",
		from: "from",
		to: "to",
		newComment: "New comment",
		actions: {
			[TimelineActionEnum.INSERT]: "created",
			[TimelineActionEnum.UPDATE]: "updated",
			[TimelineActionEnum.DELETE]: "deleted",
		},
		keys: {
			[OrderTimelineKeyEnum.status]: "status",
			[OrderTimelineKeyEnum.partnerId]: "partner",
			[OrderTimelineKeyEnum.price]: "price",
		},
		system: "System",
	},
	commentForm: {
		addComment: "Add comment",
		updateComment: "Update comment",
		inputLabel: "Comment",
		cancel: "Cancel",
	},
	header: {
		price: "Price",
		dateCreated: "Date created",
		dateNextAction: "Next action date",
		region: "Region",
		vertical: "Vertical",
	},
	status: {
		no_answer: "Declined",
		answered: "Answered",
	},
};

const orderTabsTranslations: OrderTabs = {
	edit: "Edit",
	detail: "Detail",
};

const orderCallOutcomeTranslations: OrderCallOutcome = {
	label: "Call outcome",
	acceptedCall: "Accepted",
	declinedCall: "Declined",
};

const orderHeaderButtonsTranslations: OrderHeaderButtons = {
	notifyCustomer: "Notify client",
	notifyPartner: "Notify partner",
	sendPartnerInfo: "Send Partner Info",
	deposit: "Deposit requirement",
	archive: "Archive",
	claim: "Create claim",
	createExamination: "Create examination",
};

const orderAllocationFlowTranslations: OrderAllocationFlow = {
	startButton: "Start allocation flow",
	startTitle: "Start allocation flow",
	startDescription:
		"Start partner allocation for this order after confirming details with the customer.",
	confirm: "Start",
	cancel: "Cancel",
	reallocateTitle: "Partner already assigned",
	reallocateDescription:
		"This order already has an assigned partner. Starting allocation again may reassign the order.",
	reallocateConfirm: "Reallocate",
};

const orderMutationTranslations: OrderMutations = {
	orderCallOutcomeError: "Failed to save call outcome",
	orderCallOutcomeSuccess: "Call outcome saved successfully",
	orderCommentError: "Failed to add comment",
	orderCommentSuccess: "Comment added successfully",
	orderCommentUpdateError: "Failed to update comment",
	orderCommentUpdateSuccess: "Comment updated successfully",
	orderCommentDeleteError: "Failed to delete comment",
	orderCommentDeleteSuccess: "Comment deleted successfully",
	orderEmailNotificationError: "Failed to send email notification",
	orderEmailNotificationSuccess: "Email notification sent successfully",
	orderUpdateError: "Failed to update order",
	orderUpdateSuccess: "Order updated successfully",
	customerUpdateError: "Failed to update customer",
	customerUpdateSuccess: "Customer updated successfully",
	orderCreateError: "Failed to create order",
	orderCreateSuccess: "Order created successfully",
	orderArchiveError: "Failed to archive order",
	orderArchiveSuccess: "Order archive successfully",
	orderPriceUpdateError: "Failed to update order price",
	orderPriceUpdateSuccess: "Order price updated successfully",
	orderManagerDiceError: "Failed to send order to Manager Dice",
	orderManagerDiceSuccess: "Order sent to Manager Dice successfully",
	orderAllocationFlowSuccess: "Allocation flow started successfully",
	orderPhotosUploadError: "Failed to upload photos",
	orderPhotosUploadSuccess: "Photos uploaded successfully",
};

export const orderClaimTranslations: OrderClaim = {
	fee: "Fee",
	status: "Status",
	type: "Type",
	description: "Description",
	noDescription: "No description",
	finalSolution: "Final Solution",
	dateNextAction: "Next action date",
	dateResolved: "Date Resolved",
	saveButton: "Save",
	cancelButton: "Cancel",
	orderId: "Order ID",
	resolve: "Resolve",
	reopen: "Reopen",
	partner: "Partner",
};

const orderSharedTranslations: OrderShared = {
	actions: "Actions",
	title: "Orders",
	description: "Browse the list of all orders placed.",
	edit: "Edit",
	balance: "Balance",
	detail: "Detail",
	invoice: "Payment request",
	protocol: "Protocol",
	review: "Review",
	create: "Create",
	filters: "Filters",
	search: "Search",
	reset: "Reset",
	query: "Search",
	payments: "Payments",
	copySuccess: "Order copied to clipboard",
	copy: "Copy",
	examination: "Examination",
	myOrders: "My orders",
	assignManagers: "Assign managers",
};

const orderProtocolTranslations: OrderProtocol = {
	title: "Handover protocol",
	badge: {
		accepted: "Accepted",
		autoAccepted: "Auto-accepted",
		pending: "Pending",
		declined: "Declined",
	},
	status: {
		draft: "Draft",
		sent: "Sent",
		accepted: "Accepted",
		declined: "Declined",
	},
	downloadPdf: "Download PDF",
	empty: "No protocol has been created for this order yet.",
	finalPrice: "Final price",
	priceAdjustments: "Price adjustments / notes",
	originalEstimate: "Original estimate {{value}}",
	jobStarted: "Job started",
	jobCompleted: "Job completed",
	partner: "Partner",
	client: "Client",
	address: "Address",
	automatic: "Automatic",
	pending: "Pending",
	declined: "Declined",
	notConfirmed: "—",
	checklist: "Completion checklist",
	clientComments: "Client comments",
	stageStart: "Start",
	stageHandover: "Handover",
};

const orderCommentsTableTranslations: OrderCommentsTable = {
	id: "ID",
	entityId: "Order ID",
	userId: "User ID",
	message: "Message",
	dateCreated: "Date Created",
};

const validationMessagesTranslations: ValidationMessages = {
	general: "Invalid value for field",
	integer: "Invalid integer value for field",
	string: "Invalid string value for field",
	boolean: "Invalid boolean value for field",
	enum: "Invalid value for field",
	float: "Invalid float value for field",
	dateDone: "Date done is required",
	dateStarting: "Start date is required",
	cancellationReason: "Cancellation reason is required",
	partnerRequired: "Partner is required for the order",
};

const selectOptionsTranslations: SelectOptions = {
	orderStatus: {
		pending: "Pending",
		confirmed: "Confirmed",
		in_progress: "In progress",
		cancelled: "Cancelled",
		done: "Done",
	},
	orderCancellationReason: {
		irrelevant_request: "Irrelevant request",
		did_not_reach: "Did not reach",
		no_answer: "No answer or declined",
		competition: "Competition",
		price: "Price",
		professional: "Professional",
		online_booking: "Online booking",
		other: "Other",
	},
	orderPriceChangeReason: {
		website_price: "Website price",
		manually_created_order: "Manually created order",
		admin_price: "Manager got details",
		customer_adjustements: "Customer adjustments",
		partner_adjustements: "Partner adjustments",
		examination_adjustements: "Examination adjustments",
		discount: "Discount",
		follow_up_adjustements: "Follow-up adjustments",
		protocol_agreed_price: "Protocol agreed price",
	},
	orderPartnerReviewRating: {
		satisfied: "Satisfied",
		not_satisfied: "Not satisfied",
	},
	packageType: {
		standard: "Standard",
		economy: "Economy",
		premium: "Premium",
	},
};

const orderArchiveTranslations: OrderArchive = {
	title: "Archive Order",
	description: "Do you really want to archive this order?",
	confirm: "Archive",
	cancel: "Cancel",
};

const partnerReviewFormTranslations: PartnerReviewForm = {
	addReview: "Add review",
	updateReview: "Update review",
	rating: "Rating",
	description: "Description",
	partner: "Partner",
	add: "Add",
};

const partnerReviewSharedTranslations: PartnerReviewShared = {
	title: "Reviews",
	askForReview: "Ask for review",
};

const partnerReviewMutationTranslations: PartnerReviewMutations = {
	addReviewError: "Failed to add review",
	addReviewSuccess: "Review added successfully",
	deleteReviewError: "Failed to delete review",
	deleteReviewSuccess: "Review deleted successfully",
};

const orderPhotosTranslations: OrderPhotos = {
	title: "Photos",
	empty: "No photos uploaded.",
	upload: "Upload photos",
	uploadDisabledFull: "This order already has the maximum of {{max}} photos",
	adminBadge: "Admin",
	tooMany: "You can add at most {{remaining}} more photo(s) to this order",
	fileTooLarge: "{{name}} is larger than {{maxMb}} MB",
	unsupportedType: "{{name}} is not a JPEG, PNG or WebP image",
	lightbox: "Order photo lightbox",
};

const orderTranslations: OrderTranslations = {
	editForm: editFormTranslations,
	createForm: createFormTranslations,
	detail: orderDetailTranslations,
	callOutcome: orderCallOutcomeTranslations,
	tabs: orderTabsTranslations,
	mutations: orderMutationTranslations,
	headerButtons: orderHeaderButtonsTranslations,
	allocationFlow: orderAllocationFlowTranslations,
	claim: orderClaimTranslations,
	shared: orderSharedTranslations,
	protocol: orderProtocolTranslations,
	commentsTable: orderCommentsTableTranslations,
	validationMessages: validationMessagesTranslations,
	selectOptions: selectOptionsTranslations,
	archive: orderArchiveTranslations,
	partnerReviewForm: partnerReviewFormTranslations,
	partnerReviewShared: partnerReviewSharedTranslations,
	partnerReviewMutations: partnerReviewMutationTranslations,
	photos: orderPhotosTranslations,
};

export default orderTranslations;
